import Cookies from "js-cookie";

import { ALL_GROUPS, ConsentGroup, normalizeGroupId, serializeGroups, type ConsentState } from "./groups";

/** Records the visitor's cookie choices. Strictly necessary, so itself exempt. */
export const CONSENT_COOKIE = "bsky_consent";

/** Persist ~6 months so we don't re-prompt every session. */
export const CONSENT_MAX_AGE_DAYS = 180;

/** Values written by the previous binary-consent implementation. Still in the field. */
const LEGACY_GRANTED = "granted";
const LEGACY_DENIED = "denied";

function isSecureContext(): boolean {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}

/**
 * Strict parse for our *own* cookie: every segment must be a category we know,
 * or the whole value is rejected and the visitor is re-prompted. OneTrust's live
 * value gets the tolerant `parseGroupString` instead — they own that format, and
 * an id we don't recognise there is just a category we don't use. Here, an
 * unrecognised segment means the cookie was truncated or tampered with, and
 * guessing at a partial consent record is worse than asking again.
 */
function parseOwnCookie(raw: string): Set<ConsentGroup> | null {
  const segments = raw
    .split(",")
    .map(segment => segment.trim())
    .filter(segment => segment !== "");
  if (segments.length === 0) return null;

  const granted = new Set<ConsentGroup>([ConsentGroup.Necessary]);
  for (const segment of segments) {
    const id = normalizeGroupId(segment);
    if (!id) return null;
    granted.add(id);
  }
  return granted;
}

const UNDECIDED: ConsentState = { decided: false, granted: new Set([ConsentGroup.Necessary]) };

/**
 * Every failure path lands on undecided, which shows the banner and loads
 * nothing. Legacy binary values are translated rather than discarded so we
 * don't re-prompt visitors who already chose.
 */
export function readHomegrownConsent(): ConsentState {
  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return { decided: false, granted: new Set(UNDECIDED.granted) };

  // They accepted everything on offer at the time.
  if (raw === LEGACY_GRANTED) return { decided: true, granted: new Set(ALL_GROUPS) };
  // They accepted only what was never optional.
  if (raw === LEGACY_DENIED) return { decided: true, granted: new Set([ConsentGroup.Necessary]) };

  const granted = parseOwnCookie(raw);
  if (!granted) return { decided: false, granted: new Set(UNDECIDED.granted) };
  return { decided: true, granted };
}

/**
 * Legacy values are only translated on read, never rewritten there: a read
 * happens on every page, and rewriting would push the 180-day expiry out
 * without a fresh decision from the visitor.
 */
export function writeHomegrownConsent(groups: Iterable<ConsentGroup>): void {
  Cookies.set(CONSENT_COOKIE, serializeGroups(groups), {
    expires: CONSENT_MAX_AGE_DAYS,
    sameSite: "lax",
    secure: isSecureContext(),
    path: "/",
  });
}
